import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { TeamInboxMessagesCacheReducerActions } from '@common-reducers/DBServiceThunks';
import { waQueryVaultMessagesTool } from '@common/tools/whatsapp';
import { apiCall } from '@extension/wa-communication-layer/api/api-call-helper';

/**
 * Data hook for the /view-backup page. Pages a SINGLE storage tier (the local
 * vault OR the server DB) across ALL chats, newest first, and feeds the rows
 * into the shared team-inbox message cache so the existing bubble + avatar
 * components render them. Order is kept locally (ascending: oldest→newest, so
 * newest sits at the bottom like a chat); "load older" prepends the next page.
 *
 * The vault tier is read THROUGH the engine (the vault is IndexedDB on the
 * web.whatsapp.com origin, invisible to this website origin) via the
 * server-mode WA tool; the DB tier is the owner-scoped `wa-messages-backup`
 * find, cursor-paged.
 */

export type BackupSource = 'vault' | 'db';

/** One display row: the cache id + which chat it belongs to (for the per-row
 *  avatar/label) + send-time in seconds (for date separators + ordering). */
export interface BackupDisplayItem {
    _id: string;
    chatId: string;
    t: number;
}

/** Opaque page cursor: vault uses {t(ms), msgId}; DB uses {t(ms), id(_id)}. */
type VaultCursor = { t: number; msgId: string };
type DbCursor = { t: number; id: string };

const PAGE_SIZE = 60;

/** ms→seconds normalizer (WA slim `t` is already seconds; DB `t` is ms). */
const toSeconds = (v: number): number => (v > 1e12 ? Math.floor(v / 1000) : v);

interface UseBackupSourceResult {
    items: BackupDisplayItem[];
    hasMore: boolean;
    loading: boolean;
    /** True once the first page has resolved (even if empty). */
    loaded: boolean;
    error: string | null;
    /** Fetch the next older page and prepend it. Safe to call repeatedly. */
    loadOlder: () => void;
    /** Total rows loaded so far (for the header count). */
    count: number;
}

export function useBackupSource(source: BackupSource): UseBackupSourceResult {
    const dispatch = useAppDispatch();
    const [items, setItems] = useState<BackupDisplayItem[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Cursor + in-flight guard + dedup set live in refs so the paging callback
    // is stable and never races itself.
    const cursorRef = useRef<VaultCursor | DbCursor | null>(null);
    const inFlightRef = useRef(false);
    const doneRef = useRef(false);
    const seenIdsRef = useRef<Set<string>>(new Set());

    /** Fetch one older page from the vault tier (engine round-trip). */
    const fetchVaultPage = useCallback(async (): Promise<{ items: BackupDisplayItem[]; hasMore: boolean }> => {
        const before = cursorRef.current as VaultCursor | null;
        const res = await waQueryVaultMessagesTool({ limit: PAGE_SIZE, before: before ?? undefined });
        if (res.success === false) {
            throw new Error(res.error?.message || 'vault read failed');
        }
        const { messages, nextCursor, hasMore: more } = res.data;
        const display: BackupDisplayItem[] = [];
        const cacheRows: any[] = [];
        for (const m of messages) {
            const _id = m.waMessageKey?._serialized || m.msgId;
            if (!_id || seenIdsRef.current.has(_id)) continue;
            seenIdsRef.current.add(_id);
            const tSec = toSeconds(m.t);
            cacheRows.push({ ...m, _id, waMessageId: _id, chatId: m.chatId, timestamp: tSec, source: 'vault' });
            display.push({ _id, chatId: m.chatId, t: tSec });
        }
        if (cacheRows.length > 0) dispatch(TeamInboxMessagesCacheReducerActions.upsertMany(cacheRows));
        cursorRef.current = nextCursor;
        return { items: display, hasMore: more };
    }, [dispatch]);

    /** Fetch one older page from the server DB tier (owner-scoped find). */
    const fetchDbPage = useCallback(async (): Promise<{ items: BackupDisplayItem[]; hasMore: boolean }> => {
        const before = cursorRef.current as DbCursor | null;
        let endpoint = `/wa-messages-backup?limit=${PAGE_SIZE}`;
        if (before) {
            endpoint += `&before[t]=${encodeURIComponent(before.t)}&before[id]=${encodeURIComponent(before.id)}`;
        }
        const rows = await apiCall<any[]>('get', endpoint);
        const docs = Array.isArray(rows) ? rows : [];
        const display: BackupDisplayItem[] = [];
        const cacheRows: any[] = [];
        for (const d of docs) {
            const _id = d.msgId || String(d._id);
            if (!_id || seenIdsRef.current.has(_id)) continue;
            seenIdsRef.current.add(_id);
            const tSec = toSeconds(Number(d.t) || 0);
            cacheRows.push({
                waMessageKey: { _serialized: d.msgId },
                _id,
                waMessageId: _id,
                chatId: d.chatId,
                from: d.senderId ?? '',
                author: d.senderId ?? null,
                notifyName: d.authorName ?? null,
                type: d.type || 'chat',
                body: d.body ?? '',
                caption: d.caption ?? null,
                filename: null,
                fromMe: !!d.fromMe,
                hasAttachedMedia: !!d.hadMedia,
                t: tSec,
                timestamp: tSec,
                source: 'backup',
            });
            display.push({ _id, chatId: d.chatId, t: tSec });
        }
        if (cacheRows.length > 0) dispatch(TeamInboxMessagesCacheReducerActions.upsertMany(cacheRows));
        // Derive the next cursor from the last (oldest) doc; the DB sort is
        // {t:-1,_id:-1} so the last row is the correct strict-before anchor.
        const last = docs[docs.length - 1];
        cursorRef.current = last ? { t: Number(last.t) || 0, id: String(last._id) } : cursorRef.current;
        return { items: display, hasMore: docs.length >= PAGE_SIZE };
    }, [dispatch]);

    const loadOlder = useCallback(() => {
        if (inFlightRef.current || doneRef.current) return;
        inFlightRef.current = true;
        setLoading(true);
        setError(null);
        const run = source === 'vault' ? fetchVaultPage : fetchDbPage;
        run()
            .then(({ items: page, hasMore: more }) => {
                // Prepend older rows (each page is newest-first → reverse to
                // ascending, then splice above the existing ascending list).
                if (page.length > 0) {
                    const ascending = [...page].reverse();
                    setItems((prev) => [...ascending, ...prev]);
                }
                setHasMore(more);
                if (!more) doneRef.current = true;
            })
            .catch((e) => {
                setError(e?.message || String(e));
                setHasMore(false);
                doneRef.current = true;
            })
            .finally(() => {
                inFlightRef.current = false;
                setLoading(false);
                setLoaded(true);
            });
    }, [source, fetchVaultPage, fetchDbPage]);

    // Initial page on mount / source switch.
    useEffect(() => {
        // Reset all paging state when the source changes.
        cursorRef.current = null;
        inFlightRef.current = false;
        doneRef.current = false;
        seenIdsRef.current = new Set();
        setItems([]);
        setHasMore(true);
        setLoaded(false);
        setError(null);
        loadOlder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);

    return { items, hasMore, loading, loaded, error, loadOlder, count: items.length };
}
